import React from 'react';
import { observer } from 'mobx-react-lite';
import { Collapse } from '@bringg/react-components';
import classnames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import type { Task } from '@bringg/types';
import TooltipClusterItemButton from './tooltip-cluster-item-button/tooltip-cluster-item-button';
import TaskTooltipInfo from './task-tooltip-info/task-tooltip-info';
import useStore from '../../stores/helpers/use-context';
import { isTaskStarted } from '../../utils/bringgTypesTransformers';
import TaskTooltipActions from './task-tooltip-actions/task-tooltip-actions';
import { NO_ROUTE_ID } from '../../stores/routes-planner-store';
import TaskTooltipTitle from './task-tooltip-title/task-tooltip-title';

const Panel = Collapse.Panel;

enum ToolTipPanels {
	ACTIONS = 'actions',
	TASK_INFO = 'task_info'
}

type Props = {
	taskIds: number[];
	removeTooltip?: () => void;
	resequenceChange: (priority: number, routeId: number) => void;
	currentItemIndex: number;
	setCurrentItemIndex: (currentIndex: number) => void;
};

const TaskTooltipBody: React.FC<Props> = ({
	taskIds,
	removeTooltip,
	resequenceChange,
	currentItemIndex,
	setCurrentItemIndex
}) => {
	const routesPlannerStore = useStore();
	const { displayedRoutesList, unassignTasksOnSameRoute, openOrderPage, translations, team, routesColors } =
		useStore();

	const { tooltipTranslations } = translations;

	const handleTooltipClusterButtonClicked = (index: number) => setCurrentItemIndex(index);

	const toolTipTasks = taskIds.map(taskId => routesPlannerStore.tasksById.get(taskId)).filter(item => item !== null);

	const dataToDisplay =
		toolTipTasks.length !== taskIds.length ? ({} as Task) : toolTipTasks[currentItemIndex] ?? toolTipTasks[0];

	const hasClusterItems = taskIds.length > 1;
	const taskStarted = isTaskStarted(dataToDisplay?.status);

	const getRunColor = runId => routesColors.get(runId);

	return (
		dataToDisplay && (
			<div className="task-tooltip-body">
				<TaskTooltipTitle
					translations={tooltipTranslations}
					dataToDisplay={dataToDisplay}
					runColor={getRunColor(dataToDisplay?.run_id)}
					openOrderPage={openOrderPage}
				/>
				<div className="task-tooltip-body-content">
					<Collapse
						className="task-tooltip-body-collapse"
						defaultActiveKey={taskStarted ? ToolTipPanels.TASK_INFO : ToolTipPanels.ACTIONS}
						expandIconPosition="end"
						expandIcon={({ isActive }) => (
							<span className={classnames('tooltip-chevron', { rotate: !isActive })}>
								<BringgIcon iconName={BringgFontIcons.ChevronUp} />
							</span>
						)}
						accordion
					>
						{!taskStarted && (
							<Panel
								className="task-tooltip-body-collapse-panel"
								header={tooltipTranslations.moveTask}
								key={ToolTipPanels.ACTIONS}
							>
								<TaskTooltipActions
									routesList={displayedRoutesList}
									tooltipTranslations={tooltipTranslations}
									resequenceValues={
										!dataToDisplay || dataToDisplay.run_id === NO_ROUTE_ID
											? { priority: null, runId: null }
											: { priority: dataToDisplay.priority, runId: dataToDisplay.run_id }
									}
									color={getRunColor(dataToDisplay?.run_id)}
									onResequenceChange={resequenceChange}
									onUnassignClick={() => {
										unassignTasksOnSameRoute([dataToDisplay?.id]);
										removeTooltip?.();
									}}
								/>
							</Panel>
						)}
						<Panel
							className="task-tooltip-body-collapse-panel"
							header={tooltipTranslations.taskInfo}
							key={ToolTipPanels.TASK_INFO}
						>
							<TaskTooltipInfo
								taskToDisplay={dataToDisplay}
								tooltipTranslations={tooltipTranslations}
								taskStarted={taskStarted}
								timezone={team.time_zone}
								color={getRunColor(dataToDisplay?.run_id)}
							/>
						</Panel>
					</Collapse>
				</div>

				<div className="task-tooltip-body-footer">
					{hasClusterItems && (
						<div className="task-tooltip-body-content-row">
							{taskIds.map((id, index) => (
								<TooltipClusterItemButton
									key={id}
									taskData={toolTipTasks[index]}
									highlight={index === currentItemIndex}
									onClick={() => handleTooltipClusterButtonClicked(index)}
									color={getRunColor(dataToDisplay?.run_id)}
								/>
							))}
						</div>
					)}
				</div>
			</div>
		)
	);
};

export default observer(TaskTooltipBody);
